import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface ProgressiveImageProps {
    src: string;
    alt: string;
    className?: string;
    containerClassName?: string;
    aspectRatio?: 'square' | 'video' | 'portrait' | 'auto';
    objectFit?: 'cover' | 'contain';
    loading?: 'lazy' | 'eager';
    style?: React.CSSProperties;
    fill?: boolean;
}

const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
    src,
    alt,
    className = "",
    containerClassName = "",
    aspectRatio = 'auto',
    objectFit = 'cover',
    loading = 'lazy',
    style = {},
    fill = false
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);

    // Synchronous check for cached images
    React.useLayoutEffect(() => {
        if (imgRef.current?.complete) {
            setIsLoaded(true);
            setShouldAnimate(false);
        } else {
            setShouldAnimate(true);
        }
    }, [src]);

    const aspectClass = fill ? "" : {
        square: 'aspect-square',
        video: 'aspect-video',
        portrait: 'aspect-[3/4]',
        auto: ''
    }[aspectRatio];

    const containerStyle = fill ? { position: 'absolute', inset: 0 } as React.CSSProperties : {};

    return (
        <div
            className={`${fill ? '' : 'relative'} overflow-hidden ${aspectClass} ${containerClassName}`}
            style={{ ...containerStyle }}
        >
            {/* Shimmer/Skeleton Placeholder - Only show if not loaded and no error */}
            {!isLoaded && !error && (
                <div className="absolute inset-0 bg-zinc-900 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className="absolute inset-0 bg-zinc-900 flex items-center justify-center text-zinc-700">
                    <span className="text-[10px] font-bold uppercase tracking-widest">Error de imagen</span>
                </div>
            )}

            <img
                ref={imgRef}
                src={src}
                alt={alt}
                loading={loading}
                decoding="async"
                className={`
                    w-full h-full object-${objectFit} hover:!scale-105
                    ${shouldAnimate ? 'transition-all duration-500 ease-out' : 'transition-none'}
                    ${isLoaded ? 'opacity-100 blur-0' : 'opacity-0 blur-lg'}
                    ${className}
                `}
                onLoad={() => setIsLoaded(true)}
                onError={() => setError(true)}
                style={{
                    ...style,
                    // If a specific transform is passed (like scale), it will override Tailwind's default transform
                    // But we must ensure it doesn't conflict with our opacity transitions
                }}
            />
        </div>
    );
};

export default ProgressiveImage;
